/** Synthèse du roadbook — EF-RBK-008. */

import type { JSX } from 'preact';
import type { RoadbookDocument } from '../../core/model';
import {
  formatDateTime,
  formatDistance,
  formatDurationBreakdown,
  formatElevation,
  formatSpeed,
  NOT_AVAILABLE,
} from '../format';

export interface SummaryBlockProps {
  readonly document: RoadbookDocument;
  readonly displayedPoiCount: number;
  readonly onPurge: () => void;
}

export function SummaryBlock(props: SummaryBlockProps): JSX.Element {
  const { document } = props;
  const totals = document.results.totals;
  const track = document.route.track;

  let excluded = 0;
  let pinned = 0;
  let absent = 0;
  for (const poi of document.pois.values()) {
    if (poi.userState.excluded) excluded += 1;
    if (poi.userState.pinned) pinned += 1;
    if (poi.userState.presenceState === 'absentFromResult') absent += 1;
  }

  const breakdown =
    totals === null
      ? null
      : formatDurationBreakdown(
          totals.totalSeconds,
          totals.ridingSeconds,
          totals.pauseSeconds,
          totals.sleepSeconds,
        );

  return (
    <section class="card" aria-label="Synthèse du roadbook">
      <div class="summary-grid">
        <Item label="Longueur totale" value={formatDistance(track.lengthMeters)} />
        <Item
          label="D+ total"
          value={track.elevation.available ? formatElevation(track.elevationGainMeters) : 'indisponible'}
        />
        <Item label="Départ" value={totals === null ? NOT_AVAILABLE : formatDateTime(totals.startLocal)} />
        <Item
          label="Arrivée estimée"
          value={totals === null ? NOT_AVAILABLE : formatDateTime(totals.finishLocal)}
        />
        <Item label="Durée totale" value={breakdown?.total ?? NOT_AVAILABLE} />
        <Item
          label="Roulage / pauses / sommeil"
          value={breakdown === null ? NOT_AVAILABLE : `${breakdown.riding} / ${breakdown.pause} / ${breakdown.sleep}`}
        />
        <Item
          label="Vitesse moyenne, arrêts compris"
          value={totals === null ? NOT_AVAILABLE : formatSpeed(totals.averageSpeedKmh)}
        />
        <Item label="POI affichés" value={String(props.displayedPoiCount)} />
        <Item label="POI exclus" value={String(excluded)} />
        <Item label="POI épinglés" value={String(pinned)} />
      </div>

      {totals !== null ? (
        <p class="muted" style="margin-top:12px;">
          La vitesse moyenne est une conséquence du calcul : elle inclut les pauses et le sommeil.
        </p>
      ) : null}

      {absent > 0 ? (
        <p>
          {absent} POI marqués « absent du dernier résultat ».{' '}
          <button type="button" onClick={props.onPurge}>
            Purger les POI obsolètes
          </button>
        </p>
      ) : null}
    </section>
  );
}

function Item(props: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div class="summary-item">
      <span class="summary-label">{props.label}</span>
      <span class="summary-value">{props.value}</span>
    </div>
  );
}
